import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import ModalContainer from '@components/modals/ModalContainer';

import UploadPresentationImages from '@features/collection/components/UploadPresentationImages';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import {
	deleterCollection,
	fetchBrandWiseBuyers,
	fetchCollectionMembers,
	removeCollectionMembers,
	shareCollection
} from '@services/collectionService';
import { addPresentation } from '@services/presentationService';

import { checkHasUserType, getUserInfo } from '@utils/Util';

const ShareSuggestedCollection = ({ brandId, collectionId, collectionInfo, callback }) => {
	const [isOpenShareModal, setIsOpenShareModal] = useState(false);
	const [isOpenDiscard, setIsOpenDiscard] = useState(false);
	const [isOpenUploadModal, setIsOpenUploadModal] = useState(false);
	const [existingBuyers, setExistingBuyers] = useState([]);
	const [buyers, setBuyers] = useState([]);
	const [selectedBuyers, setSelectedBuyers] = useState([]);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const [presentationId, setPresentationId] = useState(null);

	const handleAddPresentation = async () => {
		const requestBody = {
			entityId: collectionId,
			entityType: 'COLLECTION'
		};
		const response = await addPresentation(requestBody);
		setPresentationId(response);
		return response;
	};

	useEffect(() => {
		if (isOpenShareModal) {
			handleAddPresentation();
		}
	}, [collectionId, isOpenShareModal]);

	const fetchBuyerList = async () => {
		try {
			dispatch(openLoader());
			const existingMembers = await fetchCollectionMembers(collectionId);
			const buyerList = await fetchBrandWiseBuyers(
				`?organizationType=BUYER&brandId=${brandId}&page=0&size=100`
			);
			const existingMemberIds = existingMembers?.map((item) => item.id);
			setExistingBuyers(existingMemberIds);
			setBuyers(buyerList);
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		fetchBuyerList();
	}, []);

	const renderButtonForSuggestedCollection = () => {
		let paramList = new URLSearchParams(location.search);
		const requestFrom = paramList.get('status');

		return (
			<>
				<button
					className="button outline size32 mr-1 text-nowrap"
					onClick={() => setIsOpenShareModal(true)}
				>
					Share with buyer
				</button>
				{collectionInfo?.approvalStatus === 'PENDING' && requestFrom === 'SUGGESTED_FOR_BUYER' && (
					<button className="button outline size32" onClick={() => setIsOpenDiscard(true)}>
						Discard
					</button>
				)}
			</>
		);
	};

	const isPermitForShare = () => {
		return checkHasUserType(
			['ACCOUNT_MANAGER', 'PROJECT_MANAGER', 'MERCHANDISER', 'ADMIN'],
			getUserInfo()?.allUserTypes
		);
	};

	const onClickShare = () => {
		setIsOpenShareModal(false);
		setIsOpenUploadModal(true);
	};

	const toggleCheckbox = (id) => {
		if (selectedBuyers.includes(id)) {
			setSelectedBuyers(selectedBuyers.filter((item) => item !== id));
		} else {
			setSelectedBuyers((prev) => [...prev, id]);
		}
	};

	const removeBuyer = async (userId) => {
		try {
			dispatch(openLoader());
			await removeCollectionMembers({
				collectionId,
				userIds: [userId]
			});
			await fetchBuyerList();
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader);
		}
	};

	const shareCollectionToBuyers = async () => {
		try {
			dispatch(openLoader());
			await shareCollection({
				collectionId,
				userIds: selectedBuyers
			});
			await fetchBuyerList();
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader);
		}
	};

	const renderBuyerList = () => {
		return buyers?.map((item) => {
			return (
				<div
					className="single-person d-flex align-items-center justify-content-between"
					key={`buyer_${item.id}`}
				>
					<div className="d-flex align-items-center">
						{!existingBuyers.includes(item.id) && isPermitForShare() && (
							<div className="custom-chekbox">
								<div className="form-group m-0">
									<input
										type="checkbox"
										name="Company"
										id={`user_${item.id}`}
										value={item.id}
										onChange={() => toggleCheckbox(item.id)}
										checked={selectedBuyers.includes(item.id)}
									/>
									<label htmlFor={`user_${item.id}`} />
								</div>
							</div>
						)}
						<div className="person-info d-flex align-items-center">
							<div className="image">
								<img
									src={
										item?.profilePicDocument?.docUrl ||
										`${process.env.PUBLIC_URL}/images/pro_pic_default.svg`
									}
									alt="person"
								/>
							</div>
							<div className="details-info">
								<div className="name">
									<p>{item?.name}</p>
									<p className="email">{item?.email}</p>
								</div>
							</div>
						</div>
					</div>
					{existingBuyers.includes(item.id) && (
						<div>
							<span>Shared</span>
							{isPermitForShare() && (
								<button
									className="button outline ml-12 size32"
									onClick={() => removeBuyer(item.id)}
								>
									Remove
								</button>
							)}
						</div>
					)}
				</div>
			);
		});
	};

	const discardCollection = async () => {
		try {
			dispatch(openLoader());
			await deleterCollection({ id: collectionId });
			dispatch(closeLoader());
			navigate('/collections/list');
		} catch {
			dispatch(closeLoader());
		}
	};

	return (
		<>
			{renderButtonForSuggestedCollection()}
			{isOpenShareModal && (
				<ModalContainer onCloseModal={() => setIsOpenShareModal(false)}>
					<div className="common-popup share-with-buyer-modal shadow-24dp">
						<div className="common-popup-header d-flex justify-content-between">
							<div className="popup-tilte">
								<h3 className="semibold-16 mb-0">Step 1: Share with buyer</h3>
							</div>
						</div>
						<div className="common-popup-body">
							<div className="team-section scroll-y-label">
								{buyers.length > 0 && renderBuyerList()}
								{buyers.length === 0 && (
									<div className="py-5 mt-5 text-center" role="alert">
										<p className="regular-14 gray_dark_03 weight-500 mt-5">
											No buyer found
										</p>
									</div>
								)}
							</div>
							<div className="buttons pt-3">
								<button
									className="button gray-outline size32"
									onClick={() => {
										setSelectedBuyers([]);
										setIsOpenShareModal(false);
									}}
								>
									Cancel
								</button>
								<button
									className="button size32 ml-2"
									disabled={selectedBuyers.length === 0}
									onClick={onClickShare}
								>
									Share
								</button>
							</div>
						</div>
					</div>
				</ModalContainer>
			)}
			{isOpenDiscard && (
				<ModalContainer onCloseModal={() => setIsOpenDiscard(false)}>
					<div className="common-popup shadow-24dp">
						<div className="common-popup-header d-flex justify-content-between">
							<div className="popup-tilte">
								<h3 className="semibold-16 mb-0">Discard Collection</h3>
							</div>
							<div className="close-btn" onClick={() => setIsOpenDiscard(false)}>
								<img src={`${process.env.PUBLIC_URL}/icons/close.svg`} alt="close" />
							</div>
						</div>
						<div className="common-popup-body text-center">
							<p className="regular-14 mb-4">Do you want to discard this whole collection?</p>
							<div className="buttons text-center">
								<button
									className="button gray-outline size32"
									onClick={() => setIsOpenDiscard(false)}
								>
									Cancel
								</button>
								<button className="button size32 ml-2" onClick={discardCollection}>
									Yes
								</button>
							</div>
						</div>
					</div>
				</ModalContainer>
			)}
			{isOpenUploadModal && (
				<UploadPresentationImages
					onClose={() => {
						setSelectedBuyers([]);
						setIsOpenUploadModal(false);
					}}
					title="Step 2: Upload Cover photo for presentation"
					shareCollection={shareCollectionToBuyers}
					callBack={callback}
					collectionInfo={collectionInfo}
					isEdit={selectedBuyers.length === 0}
					isApprovedRequired={collectionInfo?.approvalStatus === 'PENDING'}
					presentationId={presentationId}
				/>
			)}
		</>
	);
};

export default ShareSuggestedCollection;
